import React from 'react';
import { Button, Flex, Select, Text, TextArea, TextField, Tooltip, TooltipTrigger, useToast, TagBadge, type SelectOption, badgeVariantToTagColor } from '@chatsdet/ui';
import type { ApiCatalogDetail, ApiCatalogCredentialStats, ApiTestResponse, ApiQueryParamDef } from '@chatsdet/types';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useExecuteApiTest } from '../../../features/api-catalog/api';
import { Play, Warning, ArrowsClockwise, Plus, Trash } from '@phosphor-icons/react';
import { statusBadgeVariant, CopyButton } from '../../../features/api-catalog/components/shared';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'] as const;
const METHOD_OPTIONS: SelectOption[] = HTTP_METHODS.map((m) => ({ id: m, label: m }));

type CredentialOption = { id: string; label: string };

type Props = {
  detail: ApiCatalogDetail;
  credentials: ApiCatalogCredentialStats[];
  credentialNameMap: Map<string, string>;
  allCredentialOptions?: CredentialOption[];
};

type QueryParam = { key: string; value: string };

/** Parse query params from a URL string, preserving original encoding */
const parseQueryParams = (urlStr: string): QueryParam[] => {
  try {
    const url = new URL(urlStr);
    const rawQuery = url.search.slice(1); // drop leading ?
    if (!rawQuery) return [];
    return rawQuery.split('&').map((pair) => {
      const eqIdx = pair.indexOf('=');
      if (eqIdx === -1) return { key: decodeURIComponent(pair), value: '' };
      return {
        key: decodeURIComponent(pair.slice(0, eqIdx)),
        value: decodeURIComponent(pair.slice(eqIdx + 1)),
      };
    });
  } catch {
    return [];
  }
};

/** Minimally encode a query param value — only escape characters that break
 *  query string structure (&, =, #) and spaces. Preserves [], {}, :, etc. */
const minimalEncode = (value: string): string =>
  value
    .replace(/%/g, '%25')
    .replace(/&/g, '%26')
    .replace(/=/g, '%3D')
    .replace(/#/g, '%23')
    .replace(/ /g, '%20');

/** Apply query params back onto a URL string, preserving special characters. */
const applyQueryParams = (urlStr: string, params: QueryParam[]): string => {
  try {
    const url = new URL(urlStr);
    const filtered = params.filter(({ key }) => key.trim());
    if (filtered.length === 0) {
      return `${url.origin}${url.pathname}`;
    }
    const qs = filtered
      .map(({ key, value }) => `${minimalEncode(key)}=${minimalEncode(value)}`)
      .join('&');
    return `${url.origin}${url.pathname}?${qs}`;
  } catch {
    return urlStr;
  }
};

const formatJson = (value: string | null): string | null => {
  if (!value) return null;
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

export function ApiTestPanel({ detail, credentials, credentialNameMap, allCredentialOptions }: Props) {
  const { toast } = useToast();
  const executeTestMutation = useExecuteApiTest();

  const [selectedCredentialId, setSelectedCredentialId] = React.useState<string | null>(null);
  const [method, setMethod] = React.useState<string>(detail.method || 'GET');
  const [url, setUrl] = React.useState<string>(detail.exampleUrl || '');
  const [requestBody, setRequestBody] = React.useState<string>(
    detail.latestOccurrence?.requestBody || ''
  );
  const [queryParams, setQueryParams] = React.useState<QueryParam[]>(() =>
    parseQueryParams(detail.exampleUrl || '')
  );
  const [testResult, setTestResult] = React.useState<ApiTestResponse['data'] | null>(null);
  const [testError, setTestError] = React.useState<string | null>(null);

  // Build a lookup of schema defs by param name
  const schemaMap = React.useMemo(() => {
    const map = new Map<string, ApiQueryParamDef>();
    for (const def of detail.queryParamSchema ?? []) {
      map.set(def.name, def);
    }
    return map;
  }, [detail.queryParamSchema]);

  // Reset when detail changes
  React.useEffect(() => {
    setMethod(detail.method || 'GET');
    setUrl(detail.exampleUrl || '');
    // Merge URL params with schema-defined params
    const urlParams = parseQueryParams(detail.exampleUrl || '');
    const urlParamNames = new Set(urlParams.map((p) => p.key));
    const schemaParams = (detail.queryParamSchema ?? [])
      .filter((def) => !urlParamNames.has(def.name))
      .map((def) => ({ key: def.name, value: def.defaultValue ?? '' }));
    setQueryParams([...urlParams, ...schemaParams]);
    setRequestBody(detail.latestOccurrence?.requestBody || '');
    setTestResult(null);
    setTestError(null);
  }, [detail.id]);

  // When query params change, update the URL
  const syncingFromUrl = React.useRef(false);
  const handleQueryParamChange = React.useCallback(
    (index: number, field: 'key' | 'value', val: string) => {
      setQueryParams((prev) => {
        const next = prev.map((p, i) => (i === index ? { ...p, [field]: val } : p));
        // Sync back to URL
        syncingFromUrl.current = true;
        setUrl((prevUrl) => applyQueryParams(prevUrl, next));
        return next;
      });
    },
    []
  );

  const handleAddQueryParam = React.useCallback(() => {
    setQueryParams((prev) => [...prev, { key: '', value: '' }]);
  }, []);

  const handleRemoveQueryParam = React.useCallback(
    (index: number) => {
      setQueryParams((prev) => {
        const next = prev.filter((_, i) => i !== index);
        syncingFromUrl.current = true;
        setUrl((prevUrl) => applyQueryParams(prevUrl, next));
        return next;
      });
    },
    []
  );

  // When the URL field is edited directly, re-parse query params
  const handleUrlChange = React.useCallback((val: string) => {
    setUrl(val);
    if (!syncingFromUrl.current) {
      setQueryParams(parseQueryParams(val));
    }
    syncingFromUrl.current = false;
  }, []);

  const handleExecuteTest = async () => {
    setTestResult(null);
    setTestError(null);

    const result = await executeTestMutation.mutateAsync({
      requestId: detail.id,
      options: {
        credentialId: selectedCredentialId,
        method: method !== detail.method ? method : null,
        urlOverride: url !== detail.exampleUrl ? url : null,
        bodyOverride: requestBody !== (detail.latestOccurrence?.requestBody || '') ? requestBody : null,
      },
    });

    if (result.ok) {
      setTestResult(result.data);
      toast.success(`Request completed: ${result.data.statusCode} ${result.data.statusText}`);
    } else {
      setTestError(result.error);
      toast.error(`Test failed: ${result.error}`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Flex direction="column" gap="md">
          <Flex gap="sm" align="end">
            <div className="w-[100px] shrink-0">
              <Select
                aria-label="HTTP Method"
                items={METHOD_OPTIONS}
                selectedKey={method}
                onSelectionChange={(key) => setMethod(key as string)}
              />
            </div>
            <div className="flex-1">
              <TextField
                aria-label="Request URL"
                value={url}
                onChange={handleUrlChange}
                placeholder="https://api.example.com/endpoint"
              />
            </div>
            <Button
              variant="primary"
              onPress={handleExecuteTest}
              isDisabled={executeTestMutation.isPending || !url}
              iconLeft={
                executeTestMutation.isPending ? (
                  <ArrowsClockwise size={16} className="animate-spin" />
                ) : (
                  <Play size={16} weight="fill" />
                )
              }
              className="shrink-0"
            >
              {executeTestMutation.isPending ? 'Sending...' : 'Send'}
            </Button>
          </Flex>

          <div>
            <Text size="sm" className="text-muted-foreground mb-1">
              Credential
            </Text>
            <Select
              aria-label="Credential"
              items={[
                { id: 'none', label: 'No credential (public)' },
                ...(allCredentialOptions && allCredentialOptions.length > 0
                  ? allCredentialOptions
                  : credentials.map((cred) => ({
                      id: cred.credentialId,
                      label: credentialNameMap.get(cred.credentialId) || cred.credentialId,
                    }))),
              ] as SelectOption[]}
              selectedKey={selectedCredentialId || 'none'}
              onSelectionChange={(key) =>
                setSelectedCredentialId(key === 'none' ? null : (key as string))
              }
            />
          </div>

          <div>
            <Flex align="center" justify="between" className="mb-1">
              <Text size="sm" className="text-muted-foreground">
                Query Parameters
              </Text>
              <Button
                variant="ghost"
                size="sm"
                onPress={handleAddQueryParam}
                iconLeft={<Plus size={14} />}
              >
                Add
              </Button>
            </Flex>
            {queryParams.length > 0 ? (
              <div className="space-y-2">
                {queryParams.map((param, index) => {
                  const schemaDef = schemaMap.get(param.key);
                  return (
                    <div key={index} className="space-y-1">
                      <Flex gap="sm" align="center">
                        <TextField
                          aria-label={`Parameter ${index + 1} key`}
                          value={param.key}
                          onChange={(val) => handleQueryParamChange(index, 'key', val)}
                          placeholder="key"
                          className="flex-1 font-mono"
                        />
                        <TextField
                          aria-label={`Parameter ${index + 1} value`}
                          value={param.value}
                          onChange={(val) => handleQueryParamChange(index, 'value', val)}
                          placeholder={schemaDef?.defaultValue || 'value'}
                          className="flex-1 font-mono"
                        />
                        <Button
                          variant="icon"
                          iconOnly
                          size="sm"
                          onPress={() => handleRemoveQueryParam(index)}
                          tooltip="Remove parameter"
                        >
                          <Trash size={14} />
                        </Button>
                      </Flex>
                      {schemaDef && (
                        <Flex gap="xs" align="center" className="pl-1">
                          <TagBadge name={schemaDef.type} color="gray" hideDot size="sm" className="text-[10px]" />
                          {schemaDef.required && (
                            <TagBadge name="required" color="red" hideDot size="sm" className="text-[10px]" />
                          )}
                          {schemaDef.description && (
                            <Text size="xs" className="text-muted-foreground">
                              {schemaDef.description}
                            </Text>
                          )}
                        </Flex>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <Text size="xs" className="text-muted-foreground">
                No query parameters. Click "Add" to add one.
              </Text>
            )}
          </div>

          {['POST', 'PUT', 'PATCH'].includes(method) && (
            <div>
              <Text size="sm" className="text-muted-foreground mb-1">
                Request Body (JSON)
              </Text>
              <TextArea
                aria-label="Request Body"
                value={requestBody}
                onChange={setRequestBody}
                placeholder='{"key": "value"}'
                className="font-mono text-sm"
                rows={6}
              />
            </div>
          )}

        </Flex>
      </div>

      {testError && (
        <div className="bg-red-500/5 px-4 py-3 rounded space-y-1">
          <Flex align="center" gap="sm">
            <Warning size={16} weight="fill" className="text-red-500" />
            <Text size="sm" weight="medium" className="text-red-700">
              Error
            </Text>
          </Flex>
          <Text size="sm" className="text-red-700">{testError}</Text>
        </div>
      )}

      {executeTestMutation.isPending && (
        <div className="space-y-4 animate-pulse">
          <Flex align="center" justify="between">
            <div className="h-5 w-20 bg-muted rounded" />
            <Flex align="center" gap="sm">
              <div className="h-5 w-16 bg-muted rounded" />
              <div className="h-4 w-10 bg-muted rounded" />
            </Flex>
          </Flex>
          <div className="h-4 w-32 bg-muted rounded" />
          <div className="h-40 bg-muted/50 rounded-md" />
        </div>
      )}

      {testResult && !executeTestMutation.isPending && (
        <div className="border-t border-border pt-4 space-y-4">
          <Flex align="center" justify="between">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Response
            </span>
            <Flex align="center" gap="sm">
              <TagBadge name={<>{testResult.statusCode} {testResult.statusText}</>} color={badgeVariantToTagColor(statusBadgeVariant(testResult.statusCode))} hideDot size="sm" />
              <TooltipTrigger delay={100} offset={8}>
                <Text size="xs" className="text-muted-foreground cursor-default">
                  {testResult.durationMs}ms
                </Text>
                <Tooltip>Response time</Tooltip>
              </TooltipTrigger>
            </Flex>
          </Flex>

          <div>
            <details className="cursor-pointer">
              <summary className="text-xs text-muted-foreground mb-2">
                Headers ({Object.keys(testResult.headers).length})
              </summary>
              <div className="p-3 bg-slate-950 border border-slate-800 rounded-md max-h-40 overflow-auto">
                <pre className="text-xs text-slate-300 font-mono">
                  {Object.entries(testResult.headers)
                    .map(([key, value]) => `${key}: ${value}`)
                    .join('\n')}
                </pre>
              </div>
            </details>
          </div>

          <div>
            <Text size="xs" className="text-muted-foreground mb-2">
              Body
            </Text>
            {testResult.body ? (
              <div className="relative group/json">
                <CopyButton value={formatJson(testResult.body) || testResult.body} label="response body" />
                <div className="p-3 bg-slate-950 border border-slate-800 rounded-md max-h-80 overflow-auto">
                  <SyntaxHighlighter
                    language="json"
                    style={vscDarkPlus}
                    customStyle={{ margin: 0, background: 'transparent' }}
                    wrapLongLines
                  >
                    {formatJson(testResult.body) || testResult.body}
                  </SyntaxHighlighter>
                </div>
              </div>
            ) : (
              <Text className="text-muted-foreground text-xs">No response body</Text>
            )}
          </div>
        </div>
      )}

      {!testResult && !testError && !executeTestMutation.isPending && (
        <Text className="text-muted-foreground text-sm text-center py-8">
          Configure the request above and click "Send" to test this endpoint.
        </Text>
      )}
    </div>
  );
}
